import { useTranslation } from 'react-i18next';
import Modal from '@/components/common/modal/modal';
import { useIsDesktop } from '@/hooks/use-user-platform';
import type { IncomingDialogPrompt, Prompt } from '@/stores/prompter';
import cn from '@/utils/merge-class-names';

interface Props {
  prompt: Prompt & IncomingDialogPrompt;
}

export default function ActionSheetPrompt({ prompt }: Props) {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();

  const [left = 0, top = 0] = prompt.meta?.position ?? [];

  return isDesktop ? (
    <div
      className={cn('absolute z-50', { '-translate-x-full': left > window.innerWidth - 160 })}
      style={{ left, top }}>
      <div className='flex-col flex rounded-lg bg-neutral-0 min-w-40 w-fit p-2 shadow-modal z-20'>
        {prompt.resolutionButtons
          ?.filter((button) => !button.hidden)
          .map((button, index) => (
            <button
              key={`${button.resolution}-${index}`}
              type='button'
              disabled={prompt.isLoading}
              className={cn('btn btn-ghost w-full font-normal', {
                'btn-error': button.type === 'destructive',
                'pointer-events-none': button.type === 'informative',
              })}
              onClick={() => prompt.resolvers.resolve(button.resolution)}>
              {prompt.isLoading ? <span className='loading loading-spinner loading-sm' /> : button.label}
            </button>
          ))}

        <button
          type='button'
          disabled={prompt.isLoading}
          className='btn btn-ghost w-full font-normal'
          onClick={() => prompt.resolvers.resolve(false)}>
          {prompt.rejectButtonLabel ?? t('common.cancel')}
        </button>
      </div>
    </div>
  ) : (
    <Modal
      key={prompt.key}
      defaultOpen
      clickOutsideToClose
      className='justify-end'
      contentClassName='w-full p-0 bg-transparent shadow-none'
      onClose={() => prompt.resolvers.resolve(false)}>
      <div className='mb-4 mt-6 flex justify-end gap-2 flex-col items-stretch'>
        <div className='join join-vertical rounded-2xl'>
          {prompt.resolutionButtons
            ?.filter((button) => !button.hidden)
            .map((button, index) => (
              <button
                key={`${button.resolution}-${index}`}
                type='button'
                disabled={prompt.isLoading}
                className={cn(
                  'join-item btn h-16 bg-base-100/75 my-px text-[#007aff] btn-ghost w-full font-normal rounded-2xl border-transparent border-b-[0.33px] border-b-[#3C3C435C] [&:last-child]:border-b-0',
                  {
                    'btn-error': button.type === 'destructive',
                    'btn-ghost pointer-events-none': button.type === 'informative',
                  },
                )}
                onClick={() => prompt.resolvers.resolve(button.resolution)}>
                {prompt.isLoading ? <span className='loading loading-spinner loading-sm' /> : button.label}
              </button>
            ))}
        </div>

        <button
          type='button'
          disabled={prompt.isLoading}
          className='btn h-16 mt-2 w-full bg-base-100/75 btn-ghost font-bold text-[#007aff] rounded-2xl'
          onClick={() => prompt.resolvers.resolve(false)}>
          {prompt.rejectButtonLabel ?? t('common.cancel')}
        </button>
      </div>
    </Modal>
  );
}
